import { useState, useEffect } from "react";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Plus, Edit, Trash2, Clock, Users, Calendar, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format, addDays, startOfWeek, endOfWeek } from "date-fns";
import { useTranslation } from "react-i18next";

const ROLES = ["server", "host", "bartender", "chef", "manager"];
const ROLE_COLORS = {
  server: "bg-blue-100 text-blue-700", host: "bg-emerald-100 text-emerald-700",
  bartender: "bg-purple-100 text-purple-700", chef: "bg-amber-100 text-amber-700",
  manager: "bg-rose-100 text-rose-700",
};

export default function OwnerScheduling() {
  const { t } = useTranslation();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));

  const dateFrom = format(weekStart, "yyyy-MM-dd");
  const dateTo = format(endOfWeek(weekStart, { weekStartsOn: 1 }), "yyyy-MM-dd");

  useEffect(() => { fetchShifts(); }, [dateFrom, dateTo]);

  const fetchShifts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/owner/shifts?date_from=${dateFrom}&date_to=${dateTo}`);
      setShifts(res.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const handleDelete = async (shiftId) => {
    if (!window.confirm("Delete this shift?")) return;
    try {
      await axios.delete(`${API}/owner/shifts/${shiftId}`);
      setShifts(shifts.filter(s => s.shift_id !== shiftId));
      toast.success("Shift deleted");
    } catch (error) { toast.error("Failed to delete"); }
  };

  // Generate week days
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(weekStart, i);
    return { date: format(d, "yyyy-MM-dd"), label: format(d, "EEE"), dayNum: format(d, "d") };
  });

  return (
    <div data-testid="owner-scheduling-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-medium text-foreground">{t("owner_scheduling:title")}</h1>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="schedule-prev-week">&larr; Prev</Button>
          <span className="text-sm font-medium">{format(weekStart, "MMM d")} - {format(addDays(weekStart, 6), "MMM d, yyyy")}</span>
          <Button variant="outline" size="sm" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="schedule-next-week">Next &rarr;</Button>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button data-testid="schedule-add-shift-btn"><Plus className="h-4 w-4 mr-2" /> Add Shift</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Shift</DialogTitle></DialogHeader>
              <ShiftForm onSuccess={(s) => { setShifts([...shifts, s]); setShowCreate(false); }} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? <div className="animate-pulse bg-muted h-64 rounded-xl" /> : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          {/* Week header */}
          <div className="grid grid-cols-7 border-b border-border">
            {days.map(d => (
              <div key={d.date} className={`py-3 text-center border-r border-border last:border-r-0 ${d.date === format(new Date(), "yyyy-MM-dd") ? "bg-primary/5" : ""}`}>
                <p className="text-xs text-muted-foreground">{d.label}</p>
                <p className="text-lg font-medium">{d.dayNum}</p>
              </div>
            ))}
          </div>

          {/* Shifts grid */}
          <div className="grid grid-cols-7 min-h-[300px]">
            {days.map(d => {
              const dayShifts = shifts.filter(s => s.date === d.date);
              return (
                <div key={d.date} className="border-r border-border last:border-r-0 p-2 space-y-1" data-testid={`schedule-day-${d.date}`}>
                  {dayShifts.map(s => (
                    <div key={s.shift_id} className={`p-2 rounded-lg text-xs group relative ${ROLE_COLORS[s.staff_role] || "bg-slate-100 text-slate-700"}`} data-testid={`shift-${s.shift_id}`}>
                      <p className="font-medium truncate">{s.staff_name}</p>
                      <p className="opacity-70">{s.start_time}-{s.end_time}</p>
                      <button onClick={() => handleDelete(s.shift_id)} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 px-4 py-3 border-t border-border bg-muted/20">
            {ROLES.map(r => (
              <div key={r} className="flex items-center gap-1.5">
                <div className={`w-2.5 h-2.5 rounded ${ROLE_COLORS[r]?.split(" ")[0]}`} />
                <span className="text-[11px] text-muted-foreground capitalize">{r}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ShiftForm({ onSuccess }) {
  const [form, setForm] = useState({
    staff_name: "", staff_role: "server", date: format(new Date(), "yyyy-MM-dd"),
    start_time: "09:00", end_time: "17:00", notes: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, venue_id: "", notes: form.notes || null };
      const res = await axios.post(`${API}/owner/shifts`, payload);
      toast.success("Shift added");
      onSuccess(res.data);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div><Label>Staff Name *</Label><Input value={form.staff_name} onChange={e => setForm({...form, staff_name: e.target.value})} required className="mt-1" data-testid="shift-name-input" /></div>
      <div>
        <Label>Role</Label>
        <Select value={form.staff_role} onValueChange={v => setForm({...form, staff_role: v})}>
          <SelectTrigger className="mt-1" data-testid="shift-role-select"><SelectValue /></SelectTrigger>
          <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Date *</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} required className="mt-1" /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Start</Label><Input type="time" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})} className="mt-1" /></div>
        <div><Label>End</Label><Input type="time" value={form.end_time} onChange={e => setForm({...form, end_time: e.target.value})} className="mt-1" /></div>
      </div>
      <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="mt-1" placeholder="Optional notes..." /></div>
      <Button type="submit" disabled={loading} className="w-full" data-testid="shift-submit-btn">
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Add Shift
      </Button>
    </form>
  );
}
